import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Pagination from "@material-ui/lab/Pagination";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import CommonTable from "../table";
import CommonButton from "../button";
import GroupFormDrawer from "./GroupFormDrawer";
import ManageMembersDrawer from "./ManageMembersDrawer";
import ConfirmDialog from "./ConfirmDialog";
import { PAGE_SIZE, toMessages, formatDate } from "./utils";
import {
  getPermissionCatalogueAction,
  getPermissionCatalogueErrorAction,
  getGroupListAction,
  getGroupListErrorAction,
  createGroupAction,
  createGroupSuccessAction,
  createGroupErrorAction,
  updateGroupAction,
  updateGroupSuccessAction,
  updateGroupErrorAction,
  deleteGroupAction,
  deleteGroupSuccessAction,
  deleteGroupErrorAction,
} from "../../containers/dashboard/action";

const useStyles = makeStyles(() => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    gap: "16px",
    flexWrap: "wrap",
  },
  hint: {
    color: theme.colors.Violet,
    opacity: 0.7,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
  },
  createBtn: { width: "170px" },
  errorBanner: {
    backgroundColor: theme.colors.Cinderella,
    color: theme.colors.Monza,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    fontSize: "14px",
    padding: "12px 16px",
    borderRadius: "10px",
    marginBottom: "16px",
  },
  chipWrap: { display: "flex", flexWrap: "wrap", gap: "6px", maxWidth: "360px" },
  chip: {
    display: "inline-block",
    padding: "2px 9px",
    borderRadius: "999px",
    fontSize: "11.5px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.DodgerBlue,
  },
  moreChip: {
    display: "inline-block",
    padding: "2px 9px",
    borderRadius: "999px",
    fontSize: "11.5px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    backgroundColor: theme.colors.Gallery,
    color: theme.colors.Violet,
  },
  actionsCell: { display: "flex", gap: "2px", justifyContent: "flex-end" },
  pagination: { display: "flex", justifyContent: "flex-end", marginTop: "18px" },
  countText: {
    color: theme.colors.Violet,
    opacity: 0.75,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
  },
}));

function GroupsTable() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [activeGroup, setActiveGroup] = useState(null);
  const [membersGroup, setMembersGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Redux state
  const groupList = useSelector((s) => s.dashboardReducer?.groupList);
  const loading = useSelector((s) => !!s.dashboardReducer?.groupListLoading);
  const listError = useSelector((s) => s.dashboardReducer?.groupListError);
  const catalogue = useSelector((s) => s.dashboardReducer?.permissionCatalogue || []);
  const catalogueLoading = useSelector(
    (s) => !!s.dashboardReducer?.permissionCatalogueLoading
  );
  const catalogueError = useSelector((s) => s.dashboardReducer?.permissionCatalogueError);
  const mutationLoading = useSelector((s) => !!s.dashboardReducer?.groupMutationLoading);
  const fieldErrors = useSelector((s) => s.dashboardReducer?.groupFormFieldErrors);
  const createSuccess = useSelector((s) => s.dashboardReducer?.createGroupSuccess);
  const createError = useSelector((s) => s.dashboardReducer?.createGroupError);
  const updateSuccess = useSelector((s) => s.dashboardReducer?.updateGroupSuccess);
  const updateError = useSelector((s) => s.dashboardReducer?.updateGroupError);
  const deleteSuccess = useSelector((s) => s.dashboardReducer?.deleteGroupSuccess);
  const deleteError = useSelector((s) => s.dashboardReducer?.deleteGroupError);

  const groups = groupList?.data || [];
  const count = groupList?.count || 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const refetch = useCallback(
    (p) => dispatch(getGroupListAction({ page: p, page_size: PAGE_SIZE })),
    [dispatch]
  );

  // Initial + page fetches, and the permission catalogue (once).
  useEffect(() => {
    refetch(page);
  }, [refetch, page]);

  useEffect(() => {
    dispatch(getPermissionCatalogueAction());
  }, [dispatch]);

  /* ----------------------- notistack + state resets ---------------------- */
  useEffect(() => {
    if (!listError) return;
    toMessages(listError, "Unable to load role groups.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(getGroupListErrorAction(null));
  }, [listError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!catalogueError) return;
    toMessages(catalogueError, "Unable to load permissions.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(getPermissionCatalogueErrorAction(null));
  }, [catalogueError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!createSuccess) return;
    enqueueSnackbar("Role group created.", { variant: "success" });
    dispatch(createGroupSuccessAction(null));
    setDrawerOpen(false);
    setActiveGroup(null);
    setPage(1);
    refetch(1);
  }, [createSuccess, enqueueSnackbar, dispatch, refetch]);

  useEffect(() => {
    if (!createError) return;
    toMessages(createError, "Unable to create group.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(createGroupErrorAction(null, fieldErrors));
  }, [createError, enqueueSnackbar, dispatch, fieldErrors]);

  useEffect(() => {
    if (!updateSuccess) return;
    enqueueSnackbar("Role group updated.", { variant: "success" });
    dispatch(updateGroupSuccessAction(null));
    setDrawerOpen(false);
    setActiveGroup(null);
    refetch(page);
  }, [updateSuccess, enqueueSnackbar, dispatch, refetch, page]);

  useEffect(() => {
    if (!updateError) return;
    toMessages(updateError, "Unable to update group.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(updateGroupErrorAction(null, fieldErrors));
  }, [updateError, enqueueSnackbar, dispatch, fieldErrors]);

  useEffect(() => {
    if (!deleteSuccess) return;
    enqueueSnackbar("Role group deleted.", { variant: "success" });
    dispatch(deleteGroupSuccessAction(null));
    setDeleteTarget(null);
    const remaining = groups.length - 1;
    const nextPage = remaining <= 0 && page > 1 ? page - 1 : page;
    if (nextPage !== page) setPage(nextPage);
    else refetch(page);
  }, [deleteSuccess, enqueueSnackbar, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deleteError) return;
    toMessages(deleteError, "Unable to delete group.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(deleteGroupErrorAction(null));
    setDeleteTarget(null);
  }, [deleteError, enqueueSnackbar, dispatch]);

  /* ------------------------------ handlers ------------------------------- */
  const openCreate = () => {
    setDrawerMode("create");
    setActiveGroup(null);
    setDrawerOpen(true);
  };
  const openEdit = (group) => {
    setDrawerMode("edit");
    setActiveGroup(group);
    setDrawerOpen(true);
  };
  const handleSubmit = (body) => {
    if (drawerMode === "edit" && activeGroup) {
      dispatch(updateGroupAction(body, activeGroup.id));
    } else {
      dispatch(createGroupAction(body));
    }
  };

  const columns = useMemo(
    () => [
      { Header: "Name", accessor: "name", Cell: (v) => v || "—" },
      {
        Header: "Description",
        accessor: "description",
        Cell: (v) => v || "—",
      },
      {
        Header: "Permissions",
        accessor: "permissions",
        Cell: (perms) => {
          const list = Array.isArray(perms) ? perms : [];
          if (!list.length) return <span style={{ opacity: 0.5 }}>None</span>;
          const shown = list.slice(0, 3);
          const extra = list.length - shown.length;
          return (
            <div className={classes.chipWrap}>
              {shown.map((code) => (
                <span key={code} className={classes.chip}>
                  {code}
                </span>
              ))}
              {extra > 0 ? (
                <span className={classes.moreChip} title={list.join(", ")}>
                  +{extra} more
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        Header: "Members",
        accessor: "member_count",
        Cell: (v) => v ?? 0,
      },
      {
        Header: "Created",
        accessor: "created_at",
        Cell: (v) => formatDate(v),
      },
      {
        Header: "",
        accessor: "id",
        Cell: (_v, row) => (
          <div className={classes.actionsCell}>
            <Tooltip title="Edit">
              <IconButton size="small" aria-label={`Edit ${row.name}`} onClick={() => openEdit(row)}>
                <EditOutlinedIcon fontSize="small" style={{ color: theme.colors.DodgerBlue }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage members">
              <IconButton
                size="small"
                aria-label={`Manage members of ${row.name}`}
                onClick={() => setMembersGroup(row)}
              >
                <GroupOutlinedIcon fontSize="small" style={{ color: theme.colors.Violet }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" aria-label={`Delete ${row.name}`} onClick={() => setDeleteTarget(row)}>
                <DeleteOutlineIcon fontSize="small" style={{ color: theme.colors.RejectRed }} />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [classes] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      <div className={classes.toolbar}>
        <span className={classes.hint}>
          Define backoffice roles, the capabilities they grant, and who belongs to them.
        </span>
        <div className={classes.createBtn}>
          <CommonButton
            type="button"
            text="Create group"
            stylevariant="primary"
            fontSize="15px"
            onClick={openCreate}
          />
        </div>
      </div>

      {listError ? (
        <div className={classes.errorBanner}>
          {toMessages(listError, "Unable to load role groups.")[0]}
        </div>
      ) : null}

      <CommonTable columns={columns} data={groups} loading={loading} fullRadius />

      {count > 0 ? (
        <div className={classes.pagination}>
          <span className={classes.countText} style={{ marginRight: "16px" }}>
            {count} group{count === 1 ? "" : "s"}
          </span>
          <Pagination
            count={totalPages}
            page={page}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            onChange={(_, value) => setPage(value)}
          />
        </div>
      ) : null}

      <GroupFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        group={activeGroup}
        catalogue={catalogue}
        catalogueLoading={catalogueLoading}
        loading={mutationLoading}
        fieldErrors={fieldErrors}
        onSubmit={handleSubmit}
        onClose={() => {
          if (mutationLoading) return;
          setDrawerOpen(false);
          setActiveGroup(null);
        }}
      />

      <ManageMembersDrawer
        open={!!membersGroup}
        group={membersGroup}
        onClose={() => setMembersGroup(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete role group?"
        description={
          deleteTarget
            ? `“${deleteTarget.name}” will be deleted. This removes the role from ${
                deleteTarget.member_count ?? 0
              } user${(deleteTarget.member_count ?? 0) === 1 ? "" : "s"}.`
            : ""
        }
        confirmText="Delete group"
        loading={mutationLoading}
        onConfirm={() => deleteTarget && dispatch(deleteGroupAction(deleteTarget.id))}
        onClose={() => {
          if (mutationLoading) return;
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}

export default GroupsTable;
